import { OnlyViewHoc } from 'hocs'
import React from 'react'

import { getFio } from 'helpers/formatText'
import { useGetSupervisorsQuery } from 'redux/services/common'
import { mbonusApi } from 'redux/services/mbonus'
import { openmbApi } from 'redux/services/open_mb'
import { GetOpenMBParams } from 'redux/services/open_mb/types'
import { ApplicationParams } from 'services/api/ApplicaitionApi'

import { AppButton } from 'components/AppButton'
import AppDatePicker from 'components/AppDatePicker/AppDatePicker'
import { AppFiltersLayout } from 'components/AppFiltersLayout'
import { AppSelect } from 'components/AppSelect'
import { TextField } from 'components/TextField'
import { KG_PHONE_MAX_LENGTH } from 'components/TextFieldPhone'

import { divisionLabels } from 'constants/labels'
import { OPEN_MB_STATUS_TEXT } from 'constants/open_mb'
import { DivisionType } from 'models/common'

interface Props {
  onSubmit?(values: GetOpenMBParams): void
}

const initialParams: GetOpenMBParams = {
  search: '',
  status: '',
  start_date: '',
  end_date: '',
  supervisor: '',
  branch_type: '',
}

const defaultListValue = {
  title: 'Все',
  value: '',
}

export const Filters: React.FC<Props> = ({ onSubmit }) => {
  const [params, setParams] = React.useState<GetOpenMBParams>(initialParams)
  const { data: supervisors } = useGetSupervisorsQuery()
  const { data: branches } = mbonusApi.useGetMBonusBranchesQuery()
  const [downloadExcel, downloadExcelState] =
    openmbApi.useDownloadOpenMBExcelMutation()

  const statusList = Object.entries(OPEN_MB_STATUS_TEXT).map(
    ([value, title]) => ({
      title,
      value,
    }),
  )

  function _submit() {
    const _params = {
      ...params,
      supervisor:
        params.supervisor?.length === KG_PHONE_MAX_LENGTH
          ? params.supervisor
          : '',
    }
    onSubmit?.(_params)
  }

  function _reset() {
    setParams(initialParams)
    onSubmit?.(initialParams)
  }

  function _handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target
    setParams(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  function setParamByName(
    name: keyof ApplicationParams,
    value: string | number | boolean,
  ) {
    setParams({
      ...params,
      [name]: value,
    })
  }

  const branchesList = branches
    ?.filter(item => item.type_division === params.branch_type)
    .map(item => ({
      value: item.id,
      title: item.name,
    }))

  const selectedBranchText = params.branch_type
    ? branchesList?.find(item => item.value === params.branch)
    : undefined
  const selectedSupervisorText = getFio(
    supervisors?.results.find(s => s.phone === params.supervisor),
  )

  return (
    <AppFiltersLayout
      controls={
        <>
          <AppButton size="small" onClick={_submit}>
            Применить
          </AppButton>
          <AppButton size="small" variant="red" onClick={_reset}>
            Сбросить
          </AppButton>
          <OnlyViewHoc>
            <AppButton
              onClick={() => downloadExcel(params)}
              disabled={downloadExcelState.isLoading}
              variant="siren_dashed_border"
              size="small"
            >
              Скачать Excel
            </AppButton>
          </OnlyViewHoc>
        </>
      }
    >
      <TextField
        inputSize="small"
        onChange={_handleChange}
        value={params.search}
        name="search"
        label="Название"
        placeholder="Введите название"
      />

      <AppSelect
        size="extra_small"
        text={statusList.find(item => item.value === params.status)?.title}
        onChangeValues={value => setParamByName('status', value?.[0] || '')}
        list={[defaultListValue, ...statusList]}
        placeholder="Все"
        label="Статус"
      />

      <AppSelect
        size="extra_small"
        label="Тип подразделения"
        text={
          params.branch_type
            ? divisionLabels[params.branch_type as DivisionType]
            : ''
        }
        onChangeValues={([selectedValue]) => {
          setParams({
            ...params,
            branch_type: selectedValue,
            branch: undefined,
          })
        }}
        list={Object.keys(divisionLabels).map(key => ({
          value: key as DivisionType,
          title: divisionLabels[key as DivisionType],
        }))}
        placeholder="Все"
      />

      <AppSelect
        size="extra_small"
        label="Отдел"
        text={selectedBranchText?.title}
        onChangeValues={([selectedValue]) => {
          setParamByName('branch', selectedValue)
        }}
        list={branchesList || []}
        placeholder="Все"
      />

      <AppSelect
        size="extra_small"
        label="Выберите супервайзера"
        text={selectedSupervisorText}
        onChangeValues={([selectedValue]) => {
          setParamByName('supervisor', selectedValue)
        }}
        list={[
          defaultListValue,
          ...(supervisors?.results || []).map(item => ({
            value: item.phone,
            title: getFio(item),
          })),
        ]}
      />

      <AppDatePicker
        label="Начало"
        inputSize="small"
        value={params.start_date || ''}
        onchange={date => {
          setParamByName('start_date', date)
        }}
      />

      <AppDatePicker
        label="Конец"
        inputSize="small"
        value={params.end_date || ''}
        onchange={date => {
          setParamByName('end_date', date)
        }}
      />
    </AppFiltersLayout>
  )
}
